import type { RefObject } from 'react'
import { useEffect, useRef, useState } from 'react'
import {
  connectWebSocket,
  type ImportSessionUpdatedEvent,
  onImportSessionUpdated,
  resubscribeToImportSession,
  subscribeToImportSession,
  unsubscribeFromImportSession,
} from '@/lib/services/websocket'
import {
  flushImportSessionClientMetrics,
  incrementImportSessionClientMetric,
} from '../lib/import-session-client-metrics'
import {
  buildImportSessionUpdateDedupKey,
  shouldCoalesceImportSessionUpdate,
} from '../lib/import-session-update-events'

function parseWindowMs(
  value: string | undefined,
  fallback: number,
  minimum = 0,
) {
  const parsed = Number.parseInt(value ?? '', 10)
  if (Number.isFinite(parsed) && parsed >= minimum) {
    return parsed
  }
  return fallback
}

const RECONNECT_RESYNC_GAP_MS = parseWindowMs(
  import.meta.env.DT_VITE_AUDIENCE_IMPORT_RECONNECT_RESYNC_GAP_MS,
  0,
)
const VISIBILITY_RESYNC_GAP_MS = parseWindowMs(
  import.meta.env.DT_VITE_AUDIENCE_IMPORT_VISIBILITY_RESYNC_GAP_MS,
  60_000,
)
const COALESCE_WINDOW_MS = parseWindowMs(
  import.meta.env.DT_VITE_AUDIENCE_IMPORT_EVENT_COALESCE_WINDOW_MS,
  250,
)
const DEDUP_WINDOW_MS = parseWindowMs(
  import.meta.env.DT_VITE_AUDIENCE_IMPORT_EVENT_DEDUP_WINDOW_MS,
  2_000,
)
const METRIC_DEDUP_WINDOW_MS = 500
const SELF_ORIGINATED_MUTATION_REASONS = new Set<ImportSessionUpdatedEvent['reason']>([
  'session_created',
  'session_updated',
  'preview_step_completed',
  'preview_shuffled',
  'context_materials_entered',
  'context_materials_completed',
  'context_materials_updated',
  'context_materials_file_added',
  'context_materials_file_removed',
  'context_materials_retry_requested',
  'analyze_started',
  'analyze_restarted',
  'create_enqueued',
])
const recentMetricKeys = new Map<string, number>()

function shouldRecordMetric(metricKey: string, windowMs: number) {
  const now = Date.now()
  for (const [key, seenAt] of recentMetricKeys.entries()) {
    if (now - seenAt > windowMs) {
      recentMetricKeys.delete(key)
    }
  }

  const seenAt = recentMetricKeys.get(metricKey)
  if (typeof seenAt === 'number' && now - seenAt <= windowMs) {
    return false
  }

  recentMetricKeys.set(metricKey, now)
  return true
}

interface UseImportSessionWebsocketOptions {
  latestMachineVersion?: number | null
  /** Ref updated synchronously before mutations; avoids one-tick race with websocket echo. */
  lastMutationRequestIdRef?: RefObject<string | null>
  /** Fallback when ref not provided (e.g. standalone usage). */
  lastMutationRequestId?: string | null
}

export function useImportSessionWebsocket(
  sessionId: string,
  onInvalidate: (event: ImportSessionUpdatedEvent) => void | Promise<void>,
  options?: UseImportSessionWebsocketOptions,
) {
  const [isConnected, setIsConnected] = useState(false)
  const latestMachineVersion = options?.latestMachineVersion ?? null
  const externalLastMutationRequestIdRef = options?.lastMutationRequestIdRef
  const lastMutationRequestId = options?.lastMutationRequestId ?? null
  const invalidateRef = useRef(onInvalidate)
  const latestMachineVersionRef = useRef<number | null>(
    latestMachineVersion,
  )
  const internalLastMutationRequestIdRef = useRef<string | null>(
    lastMutationRequestId,
  )
  const hasConnectedOnceRef = useRef(false)
  const disconnectStartedAtRef = useRef<number | null>(null)
  const hiddenStartedAtRef = useRef<number | null>(null)
  const recentEventKeysRef = useRef<Map<string, number>>(new Map())
  const pendingCoalescedEventRef = useRef<ImportSessionUpdatedEvent | null>(null)
  const coalescedEventTimerRef = useRef<number | null>(null)

  useEffect(() => {
    invalidateRef.current = onInvalidate
  }, [onInvalidate])

  useEffect(() => {
    latestMachineVersionRef.current = latestMachineVersion
  }, [latestMachineVersion])

  useEffect(() => {
    internalLastMutationRequestIdRef.current = lastMutationRequestId
  }, [lastMutationRequestId])

  useEffect(() => {
    if (!sessionId) {
      return
    }

    const socket = connectWebSocket()
    subscribeToImportSession(sessionId)
    const dispatchInvalidate = (event: ImportSessionUpdatedEvent) => {
      if (
        shouldRecordMetric(
          `websocket_event_received:${buildImportSessionUpdateDedupKey(event)}`,
          DEDUP_WINDOW_MS,
        )
      ) {
        incrementImportSessionClientMetric('websocket_event_received', {
          sessionId,
          reason: event.reason,
        })
      }
      void invalidateRef.current(event)
    }
    const flushCoalescedEvent = () => {
      const pending = pendingCoalescedEventRef.current
      pendingCoalescedEventRef.current = null
      if (coalescedEventTimerRef.current !== null) {
        window.clearTimeout(coalescedEventTimerRef.current)
        coalescedEventTimerRef.current = null
      }
      if (pending) {
        dispatchInvalidate(pending)
      }
    }
    const shouldIgnoreEvent = (event: ImportSessionUpdatedEvent) => {
      const lastMutationRequestId =
        externalLastMutationRequestIdRef?.current ??
        internalLastMutationRequestIdRef.current
      if (
        typeof event.requestId === 'string' &&
        event.requestId.length > 0 &&
        event.requestId === lastMutationRequestId &&
        !event.routeAck &&
        SELF_ORIGINATED_MUTATION_REASONS.has(event.reason)
      ) {
        incrementImportSessionClientMetric('websocket_event_ignored', {
          sessionId,
          reason: event.reason,
          source: 'self_originated',
        })
        return true
      }

      const latestMachineVersion = latestMachineVersionRef.current
      if (
        typeof event.machineVersion === 'number' &&
        typeof latestMachineVersion === 'number' &&
        event.machineVersion < latestMachineVersion
      ) {
        incrementImportSessionClientMetric('websocket_event_ignored', {
          sessionId,
          reason: event.reason,
          source: 'known_machine_version',
        })
        return true
      }

      const dedupKey = buildImportSessionUpdateDedupKey(event)
      const now = Date.now()
      for (const [key, seenAt] of recentEventKeysRef.current.entries()) {
        if (now - seenAt > DEDUP_WINDOW_MS) {
          recentEventKeysRef.current.delete(key)
        }
      }
      const seenAt = recentEventKeysRef.current.get(dedupKey)
      if (typeof seenAt === 'number' && now - seenAt <= DEDUP_WINDOW_MS) {
        incrementImportSessionClientMetric('websocket_event_ignored', {
          sessionId,
          reason: event.reason,
          source: 'deduped',
        })
        return true
      }
      recentEventKeysRef.current.set(dedupKey, now)
      if (typeof event.machineVersion === 'number') {
        latestMachineVersionRef.current = Math.max(
          latestMachineVersionRef.current ?? 0,
          event.machineVersion,
        )
      }
      return false
    }
    const invalidate = (event: ImportSessionUpdatedEvent) => {
      if (shouldIgnoreEvent(event)) {
        return
      }
      if (shouldCoalesceImportSessionUpdate(event)) {
        pendingCoalescedEventRef.current = event
        if (coalescedEventTimerRef.current === null) {
          coalescedEventTimerRef.current = window.setTimeout(() => {
            flushCoalescedEvent()
          }, COALESCE_WINDOW_MS)
        }
        return
      }
      flushCoalescedEvent()
      dispatchInvalidate(event)
    }
    const handleConnect = () => {
      setIsConnected(true)
      resubscribeToImportSession(sessionId)
      if (
        shouldRecordMetric(
          `websocket_connection_state:${sessionId}:connected`,
          METRIC_DEDUP_WINDOW_MS,
        )
      ) {
        incrementImportSessionClientMetric('websocket_connection_state', {
          sessionId,
          state: 'connected',
        })
      }
      const disconnectedAt = disconnectStartedAtRef.current
      const shouldResync =
        hasConnectedOnceRef.current &&
        disconnectedAt !== null &&
        Date.now() - disconnectedAt >= RECONNECT_RESYNC_GAP_MS
      disconnectStartedAtRef.current = null
      hasConnectedOnceRef.current = true
      if (shouldResync) {
        if (
          shouldRecordMetric(
            `websocket_resync:${sessionId}:connect_gap`,
            METRIC_DEDUP_WINDOW_MS,
          )
        ) {
          incrementImportSessionClientMetric('websocket_resync', {
            sessionId,
            source: 'connect_gap',
          })
        }
        invalidate({
          sessionId,
          reason: 'socket_resynced_after_gap',
          timestamp: new Date().toISOString(),
          kind: 'repair',
        })
      }
    }
    const handleDisconnect = () => {
      setIsConnected(false)
      disconnectStartedAtRef.current = Date.now()
      if (
        shouldRecordMetric(
          `websocket_connection_state:${sessionId}:disconnected`,
          METRIC_DEDUP_WINDOW_MS,
        )
      ) {
        incrementImportSessionClientMetric('websocket_connection_state', {
          sessionId,
          state: 'disconnected',
        })
      }
    }
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        hiddenStartedAtRef.current = Date.now()
        void flushImportSessionClientMetrics(sessionId).catch(() => undefined)
        return
      }

      const hiddenStartedAt = hiddenStartedAtRef.current
      hiddenStartedAtRef.current = null
      if (
        hiddenStartedAt !== null &&
        Date.now() - hiddenStartedAt >= VISIBILITY_RESYNC_GAP_MS
      ) {
        if (
          shouldRecordMetric(
            `visibility_resync:${sessionId}:visibility_gap`,
            METRIC_DEDUP_WINDOW_MS,
          )
        ) {
          incrementImportSessionClientMetric('visibility_resync', {
            sessionId,
            source: 'visibility_gap',
          })
        }
        invalidate({
          sessionId,
          reason: 'visibility_resynced_after_gap',
          timestamp: new Date().toISOString(),
          kind: 'repair',
        })
      }
    }
    const handlePageHide = () => {
      void flushImportSessionClientMetrics(sessionId).catch(() => undefined)
    }
    const offUpdated = onImportSessionUpdated(sessionId, invalidate)

    if (socket.connected) {
      setIsConnected(true)
      resubscribeToImportSession(sessionId)
      if (
        shouldRecordMetric(
          `websocket_connection_state:${sessionId}:connected`,
          METRIC_DEDUP_WINDOW_MS,
        )
      ) {
        incrementImportSessionClientMetric('websocket_connection_state', {
          sessionId,
          state: 'connected',
        })
      }
      hasConnectedOnceRef.current = true
      disconnectStartedAtRef.current = null
    } else {
      setIsConnected(false)
    }
    socket.on('connect', handleConnect)
    socket.on('disconnect', handleDisconnect)
    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('pagehide', handlePageHide)

    return () => {
      if (coalescedEventTimerRef.current !== null) {
        window.clearTimeout(coalescedEventTimerRef.current)
        coalescedEventTimerRef.current = null
      }
      offUpdated?.()
      socket.off('connect', handleConnect)
      socket.off('disconnect', handleDisconnect)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('pagehide', handlePageHide)
      unsubscribeFromImportSession(sessionId)
    }
  }, [externalLastMutationRequestIdRef, sessionId])

  return {
    isConnected,
  }
}
